import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CustomSyncService } from '../../../core/services/custom-sync.service';
import { LoggerService } from '../../../core/services/logger.service';
import { NetworkStateService } from '../../../core/services/network-state.service';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { ChipComponent } from '../../../shared/components/chip/chip.component';
import { CheckboxComponent } from '../../../shared/components/form-controls/checkbox/checkbox.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

/** Offline mode toggle, manual sync, and network/server connectivity status --
 *  moved out of the old monolithic settings page into its own routed section. */
@Component({
  selector: 'app-connection-settings',
  standalone: true,
  imports: [
    FormsModule,
    BackButtonComponent,
    CardComponent,
    CardContentComponent,
    ChipComponent,
    CheckboxComponent,
    IconComponent,
    ButtonComponent,
    TranslatePipe,
  ],
  templateUrl: './connection-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./connection-settings.component.css', '../../../../css/settings-shared.css'],
})
export class ConnectionSettingsComponent {
  private networkState = inject(NetworkStateService);
  private customSync = inject(CustomSyncService);
  private logger = inject(LoggerService).getLogger('connection-settings');

  readonly stayOfflineMode = this.networkState.stayOfflineMode;
  readonly isOnline = this.networkState.isOnline;
  readonly syncConnected = this.customSync.isConnected;
  stayOfflineModeValue = this.stayOfflineMode();
  readonly canUseNetwork = computed(() => this.isOnline() && !this.stayOfflineMode());
  readonly syncingManually = signal(false);
  readonly syncError = signal<string | null>(null);
  readonly syncSuccess = signal(false);

  async toggleStayOfflineMode() {
    await this.networkState.toggleStayOfflineMode();
  }

  async triggerManualSync() {
    if (!this.isOnline()) {
      this.syncError.set('Cannot sync while offline');
      return;
    }

    if (!this.stayOfflineMode()) {
      this.syncError.set('Manual sync only available in stay offline mode');
      return;
    }

    this.syncingManually.set(true);
    this.syncError.set(null);
    this.syncSuccess.set(false);

    // Local elapsed-time measurement (both reads on this device) -- drift correction
    // doesn't apply to a self-relative duration.
    // eslint-disable-next-line no-restricted-syntax
    const startTime = Date.now();
    const minDuration = 1000; // 1 second minimum feedback time

    try {
      await this.networkState.manualSync();

      // eslint-disable-next-line no-restricted-syntax
      const elapsed = Date.now() - startTime;
      if (elapsed < minDuration) {
        await new Promise((resolve) => setTimeout(resolve, minDuration - elapsed));
      }

      this.syncSuccess.set(true);

      setTimeout(() => {
        this.syncSuccess.set(false);
      }, 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sync failed';
      this.syncError.set(errorMessage);
      this.logger.error('Manual sync error:', error);
    } finally {
      this.syncingManually.set(false);
    }
  }

  dismissSyncError() {
    this.syncError.set(null);
  }
}
